import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Layout, CheckCircle, Smartphone, Info, Upload, FileSpreadsheet } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import alertService from '../../utils/alert';

const MetaBulkCampaignForm = ({ onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [templates, setTemplates] = useState([]);
  const [step, setStep] = useState(1);
  const fileInputRef = useRef(null);

  const [campaign, setCampaign] = useState({
    name: '',
    templateName: '',
    language: 'en_US',
    contacts: [],
    variables: {} // Static variables
  });

  const [sendTiming, setSendTiming] = useState('now'); // 'now' | 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const [rawNumbers, setRawNumbers] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [uploadData, setUploadData] = useState(null); // { headers, rows }
  const [mapping, setMapping] = useState({}); // varIndex: headerName

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/meta/templates');
      setTemplates((res.data.templates || []).filter(t => t.status === 'APPROVED'));
    } catch (err) {
      console.error(err);
    }
  };

  const getVariablesCount = () => {
    if (!selectedTemplate) return 0;
    const body = selectedTemplate.components.find(c => c.type === 'BODY')?.text || '';
    const matches = body.match(/\{\{\d+\}\}/g);
    return matches ? new Set(matches).size : 0;
  };

  const varsCount = getVariablesCount();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // The Upload button lives in the header, reachable from every step —
    // jumping straight to step 2 let someone upload a file before ever
    // picking a name/template on step 1, whose guard (handleNext) then
    // never got a chance to run. Submitting from there failed server-side
    // ("Template  not found") in a way that used to close the modal anyway
    // (see handleSubmit) instead of sending the user back to fix it.
    if (!campaign.name || !campaign.templateName) {
      setSubmitError('Pick a campaign name and template first, then upload your contact list.');
      setStep(1);
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (data.length > 0) {
        const headers = data[0];
        const rows = data.slice(1);
        setUploadData({ headers, rows });
        setStep(2); // Jump to mapping/audience
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleNext = () => {
    if (step === 1 && (!campaign.name || !campaign.templateName)) return;
    if (step === 2 && !rawNumbers && !uploadData) return;
    setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const handleSubmit = async () => {
    setLoading(true);
    setSubmitError('');
    try {
      let scheduledAt = null;
      if (sendTiming === 'scheduled') {
        if (!scheduledDate || !scheduledTime) {
          setSubmitError('Pick both a date and a time to schedule this campaign.');
          setLoading(false);
          return;
        }
        scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
        if (Number.isNaN(scheduledAt.getTime()) || scheduledAt.getTime() <= Date.now()) {
          setSubmitError('Scheduled time must be in the future.');
          setLoading(false);
          return;
        }
      }

      let finalContacts = [];

      if (uploadData) {
        // Map from upload
        const phoneIdx = uploadData.headers.findIndex(h =>
          h.toLowerCase().includes('phone') ||
          h.toLowerCase().includes('number') ||
          h.toLowerCase().includes('contact')
        );

        if (phoneIdx === -1) {
          alertService.error('Error', 'Could not find a phone number column in your file.');
          setLoading(false);
          return;
        }

        finalContacts = uploadData.rows.map(row => {
          const phoneNumber = String(row[phoneIdx] || '').replace(/\D/g, '');
          const variables = [];

          for (let i = 1; i <= varsCount; i++) {
            const headerName = mapping[i];
            const colIdx = uploadData.headers.indexOf(headerName);
            const val = colIdx !== -1 ? String(row[colIdx] || '') : (campaign.variables[i] || '');
            variables.push({ type: 'text', text: val });
          }

          return { phoneNumber, variables };
        }).filter(c => c.phoneNumber.length >= 10);
      } else {
        // Map from raw text
        const commonVars = Object.keys(campaign.variables)
          .sort((a, b) => a - b)
          .map(key => ({ type: 'text', text: campaign.variables[key] }));

        finalContacts = rawNumbers.split(/[\n,]+/).map(n => ({
          phoneNumber: n.trim().replace(/\D/g, ''),
          variables: commonVars
        })).filter(c => c.phoneNumber.length >= 10);
      }

      // onClose() only runs after onSave resolves — previously onSave
      // (MetaCampaignsPage's handleCreateCampaign) swallowed its own error
      // and never rejected, so this always ran even when campaign creation
      // failed server-side (e.g. "Template not found"), closing the modal
      // and discarding everything the user had filled in as if it worked.
      await onSave({ ...campaign, contacts: finalContacts, scheduledAt: scheduledAt ? scheduledAt.toISOString() : undefined });
      onClose();
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message || 'Failed to create campaign. Your entries are unchanged — fix the issue above and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/40 sm:p-4">
      <div className="bg-white w-full max-w-6xl rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col overflow-y-auto max-h-[100dvh]">

        {/* Header */}
        <div className="h-16 px-6 flex items-center justify-between border-b border-[#D8DDE6]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#0176D3] rounded text-white">
              <Send className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-[#080707]">New Bulk Campaign</h2>
              <p className="text-[11px] text-[#706E6B]">Send a WhatsApp template to multiple contacts</p>
            </div>
          </div>
          <input type="file" ref={fileInputRef} className="hidden" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} />
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" icon={Upload} onClick={() => fileInputRef.current.click()}>Upload Excel/CSV</Button>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F6F9] rounded transition-colors" aria-label="Close">
              <X className="w-5 h-5 text-[#706E6B]" />
            </button>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex px-6 h-11 bg-[#F4F6F9] border-b border-[#D8DDE6] items-center gap-4">
          {[
            { n: 1, label: 'Name & Template' },
            { n: 2, label: 'Audience' },
            { n: 3, label: 'Review & Send' }
          ].map(s => (
            <div key={s.n} className="flex items-center gap-2">
              <div className={cn(
                "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all",
                step === s.n ? "bg-[#0176D3] text-white" :
                step > s.n ? "bg-[#2E844A] text-white" : "bg-[#D8DDE6] text-[#706E6B]"
              )}>
                {step > s.n ? <CheckCircle size={12} /> : s.n}
              </div>
              <span className={cn(
                "text-[11px] font-bold uppercase tracking-wider",
                step === s.n ? "text-[#0176D3]" : "text-[#706E6B]"
              )}>{s.label}</span>
              {s.n < 3 && <div className="w-8 h-px bg-[#D8DDE6] mx-2" />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto min-h-[500px]">

          {step === 1 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-[#706E6B] block">Campaign Name</label>
                    <input
                      type="text"
                      value={campaign.name}
                      onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                      placeholder="e.g. Summer_Broadcast_2024"
                      className="w-full h-10 px-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[13px] font-bold outline-none focus:bg-white focus:border-[#0176D3] transition-all"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] font-bold text-[#706E6B] block">Select Approved Template</label>
                    <div className="grid grid-cols-1 gap-2 max-h-[350px] overflow-y-auto pr-2">
                      {templates.map(t => (
                        <div
                          key={t._id}
                          onClick={() => {
                            setCampaign({ ...campaign, templateName: t.name, language: t.language, variables: {} });
                            setSelectedTemplate(t);
                          }}
                          className={cn(
                            "p-4 border rounded cursor-pointer transition-all",
                            campaign.templateName === t.name ? "bg-[#0176D3]/5 border-[#0176D3]" : "bg-white border-[#D8DDE6] hover:border-[#0176D3]"
                          )}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <h4 className={cn("text-[13px] font-bold truncate pr-4", campaign.templateName === t.name ? "text-[#0176D3]" : "text-[#080707]")}>{t.name}</h4>
                            {campaign.templateName === t.name && <CheckCircle size={16} className="text-[#0176D3]" />}
                          </div>
                          <p className="text-[11px] text-[#706E6B] font-bold uppercase tracking-wider">{t.language} &bull; {t.category}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-5">
                  {selectedTemplate ? (
                    <div className="space-y-6">
                       <div className="space-y-2">
                        <label className="text-[11px] font-bold text-[#706E6B] block">Message Preview</label>
                        <div className="p-6 bg-[#0B141A] rounded text-white min-h-[140px] relative">
                            <div className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-[#4FA3E3]"><Smartphone size={18} /></div>
                            <p className="text-[14px] leading-relaxed font-medium text-[#C9C9C9] pr-8">
                              {selectedTemplate.components.find(c => c.type === 'BODY')?.text.split(/(\{\{\d+\}\})/).map((part, i) => (
                                part.match(/\{\{\d+\}\}/)
                                  ? <span key={i} className="text-[#4FA3E3] font-bold mx-1">{campaign.variables[part.match(/\d+/)[0]] || part}</span>
                                  : part
                              ))}
                            </p>
                        </div>
                       </div>

                       {varsCount > 0 && (
                         <div className="space-y-4 p-5 bg-[#F4F6F9] border border-[#D8DDE6] rounded">
                           <div className="flex items-center gap-2">
                             <div className="p-1.5 bg-[#0176D3] rounded text-white"><Info size={14} /></div>
                             <h4 className="text-[12px] font-bold text-[#080707] uppercase tracking-wider">Fill Template Variables</h4>
                           </div>
                           <div className="grid grid-cols-1 gap-4">
                             {Array.from({ length: varsCount }).map((_, i) => (
                               <div key={i} className="space-y-1.5">
                                 <label className="text-[11px] font-bold text-[#706E6B]">Default value for {"{{" + (i + 1) + "}}"}</label>
                                 <input
                                   type="text"
                                   value={campaign.variables[i + 1] || ''}
                                   onChange={(e) => setCampaign({
                                     ...campaign,
                                     variables: { ...campaign.variables, [i + 1]: e.target.value }
                                   })}
                                   placeholder="Default value"
                                   className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all"
                                 />
                               </div>
                             ))}
                           </div>
                         </div>
                       )}
                    </div>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center p-10 border-2 border-dashed border-[#D8DDE6] rounded bg-[#F4F6F9]">
                      <Layout className="w-12 h-12 text-[#D8DDE6] mb-4" />
                      <h3 className="text-[14px] font-bold text-[#706E6B]">No template selected</h3>
                      <p className="text-[13px] text-[#706E6B] mt-2 text-center max-w-xs leading-relaxed">Select an approved template to see a preview.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
               {uploadData ? (
                 <div className="space-y-6">
                   <div className="flex items-center justify-between">
                     <div>
                       <h3 className="text-[16px] font-bold text-[#080707]">Map Your Data</h3>
                       <p className="text-[13px] text-[#706E6B] mt-1">Found <span className="text-[#0176D3] font-bold">{uploadData.rows.length}</span> contacts in your file.</p>
                     </div>
                     <Button variant="outline" onClick={() => setUploadData(null)}>Clear File &amp; Use Manual Entry</Button>
                   </div>

                   <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Card className="p-6 border-[#D8DDE6] bg-[#F4F6F9] space-y-4 rounded-md">
                        <h4 className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wider flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4" /> Column Mapping
                        </h4>
                        <div className="space-y-4">
                           {Array.from({ length: varsCount }).map((_, i) => (
                             <div key={i} className="space-y-1.5">
                               <label className="text-[11px] font-bold text-[#080707]">Map Variable {"{{" + (i + 1) + "}}"} to:</label>
                               <select
                                 className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] font-bold outline-none cursor-pointer"
                                 value={mapping[i + 1] || ''}
                                 onChange={(e) => setMapping({ ...mapping, [i + 1]: e.target.value })}
                               >
                                 <option value="">-- Select Column --</option>
                                 {uploadData.headers.map(h => <option key={h} value={h}>{h}</option>)}
                               </select>
                             </div>
                           ))}
                           {varsCount === 0 && <p className="text-[13px] text-[#706E6B] italic">This template has no variables to map.</p>}
                        </div>
                      </Card>

                      <div className="space-y-3">
                        <label className="text-[11px] font-bold text-[#706E6B] block">Data Preview (First 5 Rows)</label>
                        <div className="sf-table-container overflow-x-auto">
                          <table className="sf-table">
                            <thead>
                              <tr>
                                {uploadData.headers.map(h => <th key={h}>{h}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {uploadData.rows.slice(0, 5).map((row, i) => (
                                <tr key={i}>
                                  {row.map((cell, j) => <td key={j}>{cell}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                   </div>
                 </div>
               ) : (
                 <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div
                        onClick={() => fileInputRef.current.click()}
                        className="p-10 border-2 border-dashed border-[#D8DDE6] rounded bg-[#F4F6F9] flex flex-col items-center justify-center cursor-pointer hover:bg-[#0176D3]/5 hover:border-[#0176D3] transition-all"
                      >
                        <div className="w-14 h-14 rounded bg-white shadow-sm flex items-center justify-center mb-4 text-[#0176D3]">
                          <FileSpreadsheet size={26} />
                        </div>
                        <h4 className="text-[15px] font-bold text-[#080707] mb-2">Excel / CSV Import</h4>
                        <p className="text-[13px] text-[#706E6B] text-center leading-relaxed">Import contacts and map variable columns automatically.</p>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-bold text-[#706E6B] block">Manual Entry (Raw Numbers)</label>
                        <textarea
                          rows={10}
                          value={rawNumbers}
                          onChange={(e) => setRawNumbers(e.target.value)}
                          placeholder="919876543210&#10;919876543211"
                          className="w-full px-4 py-4 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-[14px] font-bold text-[#080707] outline-none focus:bg-white focus:border-[#0176D3] transition-all resize-none leading-relaxed"
                        />
                      </div>
                    </div>
                 </div>
               )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="p-6 border-[#D8DDE6] bg-[#F4F6F9] shadow-none space-y-5 rounded-md">
                  <h4 className="text-[12px] font-bold text-[#080707] uppercase tracking-wider mb-2">Campaign Summary</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-4 border-b border-[#D8DDE6]">
                      <span className="text-[13px] text-[#706E6B]">Campaign Name:</span>
                      <span className="text-[13px] font-bold text-[#080707]">{campaign.name}</span>
                    </div>
                    <div className="flex justify-between items-center pb-4 border-b border-[#D8DDE6]">
                      <span className="text-[13px] text-[#706E6B]">Template:</span>
                      <span className="text-[13px] font-bold text-[#0176D3]">{campaign.templateName}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[13px] text-[#706E6B]">Total Recipients:</span>
                      <span className="sf-badge sf-badge-manager">
                        {uploadData ? uploadData.rows.length : rawNumbers.split(/[\n,]+/).filter(n => n.trim().length >= 10).length} contacts
                      </span>
                    </div>
                  </div>
                </Card>

                <div className="space-y-4">
                   <h4 className="text-[12px] font-bold text-[#080707] uppercase tracking-wider">Final Message Preview</h4>
                   <div className="p-6 bg-[#0B141A] text-[#C9C9C9] rounded relative min-h-[160px]">
                      <div className="absolute top-4 right-4 text-[#4FA3E3]"><Smartphone size={22} /></div>
                      <p className="text-[14px] leading-relaxed font-medium pr-10">
                        {selectedTemplate?.components.find(c => c.type === 'BODY')?.text.split(/(\{\{\d+\}\})/).map((part, i) => (
                           part.match(/\{\{\d+\}\}/)
                            ? <span key={i} className="text-[#4FA3E3] font-bold mx-1">
                                {uploadData ? `[Column: ${mapping[part.match(/\d+/)[0]] || '?'}]` : (campaign.variables[part.match(/\d+/)[0]] || part)}
                              </span>
                            : part
                        ))}
                      </p>
                   </div>
                   <div className="p-4 bg-[#EBF3ED] border border-[#C4DCC8] rounded flex gap-3">
                    <CheckCircle className="text-[#2E844A] shrink-0 w-5 h-5" />
                    <div>
                      <h4 className="text-[13px] font-bold text-[#2E844A] mb-1">Ready to send</h4>
                      <p className="text-[12px] text-[#2E844A] leading-relaxed">
                        Messages will be queued for delivery via the Meta WhatsApp Cloud API. Delivery status will be available in campaign details.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Card className="p-6 border-[#D8DDE6] bg-[#F4F6F9] shadow-none space-y-4 rounded-md">
                <h4 className="text-[12px] font-bold text-[#080707] uppercase tracking-wider">When to send</h4>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setSendTiming('now')}
                    className={cn(
                      'flex-1 px-4 py-2.5 rounded text-[13px] font-bold border transition-colors',
                      sendTiming === 'now' ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#706E6B] border-[#D8DDE6] hover:bg-[#F4F6F9]'
                    )}
                  >
                    Send Now
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendTiming('scheduled')}
                    className={cn(
                      'flex-1 px-4 py-2.5 rounded text-[13px] font-bold border transition-colors',
                      sendTiming === 'scheduled' ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#706E6B] border-[#D8DDE6] hover:bg-[#F4F6F9]'
                    )}
                  >
                    Schedule for Later
                  </button>
                </div>
                {sendTiming === 'scheduled' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[11px] font-bold text-[#706E6B] block mb-1">Date</label>
                      <input
                        type="date"
                        min={new Date().toISOString().slice(0, 10)}
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        className="w-full h-9 px-3 border border-[#D8DDE6] rounded text-[13px]"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-[#706E6B] block mb-1">Time</label>
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="w-full h-9 px-3 border border-[#D8DDE6] rounded text-[13px]"
                      />
                    </div>
                    <p className="col-span-2 text-[11px] text-[#706E6B]">Times are in your browser's local timezone.</p>
                  </div>
                )}
              </Card>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="h-20 px-6 flex items-center justify-between border-t border-[#D8DDE6] bg-white shrink-0 gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Button variant="outline" className="h-9 px-4 rounded text-[13px] font-bold shrink-0" onClick={onClose} disabled={loading}>Cancel</Button>
            {submitError && (
              <p className="text-[12px] font-bold text-[#BA0517] truncate" title={submitError}>{submitError}</p>
            )}
          </div>
          <div className="flex gap-3">
            {step > 1 && <Button variant="secondary" className="h-9 px-4 rounded text-[13px] font-bold" onClick={handleBack} disabled={loading}>Back</Button>}
            {step < 3 ? (
              <Button onClick={handleNext} className="h-9 px-6 rounded text-[13px] font-bold">
                Continue to {step === 1 ? 'Audience' : 'Review'}
              </Button>
            ) : (
              <Button onClick={handleSubmit} loading={loading} className="h-9 px-6 rounded text-[13px] font-bold">
                <Send className="w-4 h-4 mr-2" />
                {sendTiming === 'scheduled' ? 'Schedule Campaign' : 'Send Campaign'}
              </Button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default MetaBulkCampaignForm;
